import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Home, 
  Settings, 
  LogOut, 
  FileText, 
  BarChart2,
  Server,
  Database,
  Shield,
  HelpCircle,
  User,
  Mail,
  Ticket
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { login } from '../../reducer/loginSlice';
import { AuthApis } from '../../apis/authApis';
import { AxiosResponse } from 'axios';

interface AdminSidebarProps {
  isMobile?: boolean;
  onCloseMobileMenu?: () => void;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  isMobile = false,
  onCloseMobileMenu = () => {} 
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userLoginData = useSelector((state: any) => state.data.login.value);
  const [collapseShow, setCollapseShow] = React.useState("hidden");

  React.useEffect(() => {
    if (!userLoginData.token) {
      removeAuth();
    }
  }, []);

  const removeAuth = () => {
    dispatch(login([]));
    navigate("/sign-in");
  };

  const logOut = () => {
    AuthApis.logout('').then(
      (response: AxiosResponse<any>) => {
        if (response?.data) {
          dispatch(login({
            email: "",
            name: "",
            password: "",
            token: "",
            data: []
          }));
          navigate('/sign-in');
        }
      }
    ).catch(function (error) {
      dispatch(login({
        email: "",
        name: "",
        password: "",
        token: "",
        data: []
      }));
      navigate('/sign-in');
    }).finally(function () {
      dispatch(login({
        email: "",
        name: "",
        password: "",
        token: "",
        data: []
      }));
      navigate('/sign-in');
    });
  };

  const menuItems = [
    { icon: <Home className="w-5 h-5" />, label: 'Dashboard', path: '/admin' },
    { icon: <Users className="w-5 h-5" />, label: 'Users', path: '/admin/users' },
    { icon: <Mail className="w-5 h-5" />, label: 'Email Campaigns', path: '/admin/email-campaigns' },
    { icon: <Ticket className="w-5 h-5" />, label: 'Access Codes', path: '/admin/access-codes' },
    { icon: <Database className="w-5 h-5" />, label: 'Plans', path: '/admin/plans' },
    { icon: <BarChart2 className="w-5 h-5" />, label: 'Reports', path: '/admin/reports' },
  ];

  return (
    <nav className="bg-white md:left-0 md:block md:fixed md:top-0 md:bottom-0 md:overflow-y-auto md:flex-row md:flex-nowrap md:overflow-hidden shadow-xl flex flex-wrap items-center justify-between relative md:w-64 z-10 py-4 pl-6 pr-2">
      <div className="md:flex-col md:items-stretch md:min-h-full md:flex-nowrap px-0 flex justify-between w-full">
        {/* Toggler */}
        <div className="flex justify-between">
          <div className="mr-56">
            <button
              className="cursor-pointer text-black opacity-50 md:hidden py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
              type="button"
              onClick={() => setCollapseShow("bg-white m-2 py-3 px-6")}
            >
              <b className="fas fa-bars"> ≡ </b>
            </button>
          </div>
          <div className="flex justify-end">
            <NavLink
              className="md:hidden block"
              to="/admin"
              onClick={onCloseMobileMenu}
            >
              <img src="/images/log.svg" alt="logo" className="w-[100px]" />
            </NavLink>
          </div>
        </div>

        {/* Collapse */}
        <div
          className={
            "bg-white md:flex md:flex-col md:items-stretch md:opacity-100 md:relative md:mt-4 md:shadow-none shadow absolute top-0 left-0 right-0 z-40 overflow-y-auto overflow-x-hidden h-auto items-center flex-1 rounded " +
            collapseShow
          }
        >
          {/* Collapse header */}
          <div className="md:min-w-full md:hidden block mb-4 border-b border-solid border-gray-200">
            <div className="flex flex-wrap">
              <div className="w-6/12">
                <NavLink
                  className="md:block text-left md:pb-2 text-gray-600 mr-0 inline-block whitespace-nowrap text-sm uppercase font-bold p-4 px-0"
                  to="/admin"
                  onClick={onCloseMobileMenu}
                >
                  <img src="/images/log.svg" alt="logo" className="w-[100px]" />
                </NavLink>
              </div>
              <div className="w-6/12 flex justify-end">
                <button
                  type="button"
                  className="cursor-pointer text-black opacity-50 md:hidden px-3 py-1 text-xl leading-none bg-transparent rounded border border-solid border-transparent"
                  onClick={() => setCollapseShow("hidden")}
                >
                  <b className="fas fa-times"> X </b>
                </button>
              </div>
            </div>
          </div>

          <div className="lg:flex md:flex hidden justify-center">
            <NavLink to="/admin" onClick={onCloseMobileMenu}>
              <img src="/images/log.svg" alt="logo" className="max-w-[120px]" />
            </NavLink>
          </div>

          {/* User info */}
          <div className="flex items-center justify-center mt-6 mb-4">
            <div className="flex items-center space-x-4 p-2">
              <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                {userLoginData?.data?.user_data?.first_name?.charAt(0) || 'A'}
              </div>
              <div>
                <h4 className="font-medium text-sm">
                  {userLoginData?.data?.user_data?.first_name || 'Admin'} {userLoginData?.data?.user_data?.last_name || 'User'}
                </h4>
                <p className="text-xs text-gray-500">{userLoginData?.data?.user_data?.email || 'admin@example.com'}</p>
                <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full mt-1">
                  {userLoginData?.data?.user_data?.role || 'superadmin'}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <ul className="md:flex-col md:min-w-full flex flex-col list-none">
            <li className="text-xs uppercase text-gray-500 font-semibold pb-2 px-4">
              Admin
            </li>
            
            {menuItems.map((item) => (
              <li key={item.path} className="items-center mb-2">
                <NavLink
                  to={item.path}
                  onClick={onCloseMobileMenu}
                  className={({ isActive }) => 
                    `flex mx-2 py-[10px] pl-[15px] gap-2 rounded-[37px] text-[12px] ${
                      isActive 
                        ? 'text-orange-500 bg-orange-50' 
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  {item.icon}
                  <span className="mt-0.5">{item.label}</span>
                </NavLink>
              </li>
            ))}
            
            <li className="text-xs uppercase text-gray-500 font-semibold pb-2 px-4 pt-6">
              Account
            </li>

            <li className="items-center mb-2">
              <NavLink
                to="/admin/profile"
                onClick={onCloseMobileMenu}
                className={({ isActive }) => 
                  `flex mx-2 py-[10px] pl-[15px] gap-2 rounded-[37px] text-[12px] ${
                    isActive 
                      ? 'text-orange-500 bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <User className="w-5 h-5" />
                <span className="mt-0.5">My Profile</span>
              </NavLink>
            </li>

            {/* <li className="items-center mb-2">
              <NavLink
                to="/admin/help"
                onClick={onCloseMobileMenu}
                className={({ isActive }) => 
                  `flex mx-2 py-[10px] pl-[15px] gap-2 rounded-[37px] text-[12px] ${
                    isActive 
                      ? 'text-orange-500 bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <HelpCircle className="w-5 h-5" />
                <span className="mt-0.5">Help</span>
              </NavLink>
            </li> */}

            <li className="items-center mt-4">
              <button
                onClick={logOut}
                className="flex items-center gap-2 text-red-600 p-3 w-full hover:bg-gray-100 rounded-lg"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Log out</span>
              </button>
            </li>
          </ul>

          <div className="mt-8 bg-blue-50 p-4 mx-2 rounded-lg">
            <div className="flex items-center mb-2">
              <Shield className="w-5 h-5 text-blue-600 mr-2" />
              <h3 className="text-sm font-medium text-blue-800">Admin Portal</h3>
            </div>
            <p className="text-xs text-blue-700 mb-2">You're using the admin dashboard with superadmin privileges.</p>
            <p className="text-xs text-blue-600">Version 1.0</p>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default AdminSidebar;